/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'
import { FormattedMessage } from 'react-intl'
import CopyToClipboard from 'react-copy-to-clipboard'

import type {
  AnyClusterPlanInfo,
  AnyResourceInfo,
  SliderInstanceType,
  StackDeployment,
} from '@modules/ui-types'
import { CuiActionButton } from '@modules/cui/ActionButton'
import type { CuiActionButtonActionProps } from '@modules/cui/ActionButton'
import PermissionsGate from '@modules/permissions-components/PermissionsGate'

import { isPlanSystemInitiated } from '@/lib/stackDeployments/selectors/fundamentals'
import {
  isPendingAttempt,
  isSuccessfulShutdownAttempt,
} from '@/lib/stackDeployments/selectors/configurationChangeAttempts'

import { getConfigForKey } from '../../store'
import DeploymentLockGate from '../DeploymentLockGate'
import RetryStackDeploymentAttemptButton from '../StackDeployments/RetryStackDeploymentAttemptButton'
import { setAdvancedEditInitialStateFromAttempt } from '../StackDeploymentEditor/EditStackDeploymentAdvanced/carryover'
import { deploymentAdvancedEditUrl } from '../../lib/urlBuilder'

import CancelPlanButton from './CancelPlanButton'
import { canCopyRawJson, getRawJson } from './StackConfigurationChangeRawPlanJsonCopyButton'

import type { FunctionComponent } from 'react'

type Props = {
  deployment: StackDeployment
  resource: AnyResourceInfo
  resourceType: SliderInstanceType
  planAttempt: AnyClusterPlanInfo
}

const StackConfigurationChangeActions: FunctionComponent<Props> = ({
  deployment,
  resource,
  resourceType,
  planAttempt,
}) => {
  const { id } = deployment
  const pending = isPendingAttempt({ planAttempt })
  const isShutdown = isSuccessfulShutdownAttempt({ planAttempt })
  const canCopy = canCopyRawJson({ planAttempt })
  const rawPlanJson = getRawJson({ planAttempt })
  const isSystemInitiated = isPlanSystemInitiated({ planAttempt })
  const isAdminconsole = getConfigForKey(`APP_NAME`) === `adminconsole`
  // in https://github.com/elastic/cloud/issues/103228 decided to hide the cancel button for system initiated plans on the UC
  // have to take into account UC specifically, otherwise all plans on ECE would be not cancellable
  const showCancelButton = isAdminconsole || !isSystemInitiated

  const copyAction = !isShutdown &&
    canCopy && {
      iconType: `copyClipboard`,
      name: (
        <FormattedMessage id='configuration-change-actions.copy-json' defaultMessage='Copy JSON' />
      ),
      wrapper: ({ children }) => <CopyToClipboard text={rawPlanJson}>{children}</CopyToClipboard>,
    }

  const reapplyAdvancedAction = !pending &&
    !isShutdown &&
    canCopy && {
      iconType: `documentEdit`,
      name: (
        <FormattedMessage
          id='configuration-change-actions.advanced-reapply'
          defaultMessage='Advanced reapply'
        />
      ),
      href: deploymentAdvancedEditUrl(id),
      onClick: () =>
        setAdvancedEditInitialStateFromAttempt({
          deployment,
          planAttempt,
          planAttemptSliderInstanceType: resourceType,
        }),
    }

  const actions: CuiActionButtonActionProps[] = []

  if (reapplyAdvancedAction) {
    actions.push(reapplyAdvancedAction)
  }

  if (copyAction) {
    actions.push(copyAction)
  }

  const primaryAction = (
    <DeploymentLockGate>
      {pending ? (
        showCancelButton && (
          <CancelPlanButton
            deployment={deployment}
            resource={resource}
            resourceType={resourceType}
          />
        )
      ) : (
        <PermissionsGate
          permissions={[
            {
              type: 'deployment',
              action: 'update',
              id: deployment.id,
            },
          ]}
        >
          {({ hasPermissions }) => (
            <RetryStackDeploymentAttemptButton
              deployment={deployment}
              sliderInstanceType={resourceType}
              planAttempt={planAttempt}
              color='primary'
              disabled={isShutdown || !hasPermissions}
            >
              <FormattedMessage
                id='configuration-change-actions.reapply'
                defaultMessage='Reapply'
              />
            </RetryStackDeploymentAttemptButton>
          )}
        </PermissionsGate>
      )}
    </DeploymentLockGate>
  )

  return (
    <CuiActionButton
      size='s'
      primaryAction={primaryAction}
      actions={actions}
      hideEmptyMenu={true}
    />
  )
}

export default StackConfigurationChangeActions
